<template>
    <main class="inventory-page__content">
        <VInventoryHeader></VInventoryHeader>

        <VContentHeaderDefault
            title="Держатели"
            :button="$_canManageInventory ? 'Добавить держателя' : ''"
            @search="onSearch"
            @button="onCreateButtonClick"
        ></VContentHeaderDefault>

        <template v-if="isLoading">
            <div class="inventory-page__loader">
                <VSpinner></VSpinner>
            </div>
        </template>

        <template v-else>
            <div class="inventory-page__filters">
                <div class="inventory-page__filter-item inventory-page__filter-item--control">
                    <label> Показывать: </label>

                    <VSelect
                        class="active-form__control control"
                        label="label"
                        :clearable="false"
                        :options="activeOptions"
                        :reduce="(option) => option.value"
                        :value="activeFilter"
                        @input="onActiveFilterSelected"
                    />
                </div>
            </div>

            <VContentTableDefault
                :rows="rows"
                :page="page"
                :total="total"
                :options="options"
                @page="onPageChange"
                @edit="onHolderClick"
                @click="onHolderClick"
            ></VContentTableDefault>
            <p class="inventory-page__empty" v-if="!rows.length">Записей справочника не найдено.</p>
        </template>

        <VInventoryHolderModal></VInventoryHolderModal>
    </main>
</template>
<script>
// Components
import VSpinner from '../../components/VProgress/VSpinner.vue';
import VSelect from '../../components/VSelect/VSelect.vue';
import VContentTableDefault from '../../components/VContentTableDefault.vue';
import VContentHeaderDefault from '../../components/VContentHeaderDefault.vue';
import VInventoryHeader from './components/VInventoryHeader.vue';
import VInventoryHolderModal from './modals/VInventoryHolderModal.vue';
import './InventoryPage.scss';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { errorMessage } from '../../../js/error';
import { paginateInventoryHolders, createInventoryHolder, updateInventoryHolder } from '../../../js/inventory-api';
import ModalNames from '../../../js/enums/ModalNames';
import InventoryHolderType from '../../../js/enums/InventoryHolderType';
import InventoryHolderTypeTitle from '../../../js/enums/InventoryHolderTypeTitle';

@Component({
    components: {
        VSpinner,
        VSelect,
        VContentTableDefault,
        VContentHeaderDefault,
        VInventoryHeader,
        VInventoryHolderModal,
    },
})
class InventoryPageHolders extends Vue {
    rows = [];
    page = 1;
    total = 1;
    isLoading = true;
    searchString = '';

    // null — «все»; отдельного значения нет, чтобы не путать с active = false.
    activeFilter = true;

    activeOptions = [
        { value: true, label: 'Только активных' },
        { value: false, label: 'Только погашенных' },
        { value: null, label: 'Всех' },
    ];

    /**
     * Колонки и действия строки.
     *
     * УДАЛЕНИЯ У ДЕРЖАТЕЛЕЙ НЕТ И НЕ БУДЕТ, поэтому deletable здесь false навсегда.
     * Запись справочника нужна истории предмета и после того, как человек ушёл: в карточке
     * и в журнале остаётся ссылка на неё. Вместо удаления запись гасят (active = false) —
     * она пропадает из выбора, но продолжает объяснять прошлое. Внешний ключ
     * inventory_items.holder_id стоит на ON DELETE RESTRICT, то есть и база настоящее
     * удаление занятой записи не пропустит.
     *
     * «На руках» — не украшение: погасить держателя, за которым числится техника, сервер
     * не даст (InventoryHolderService), и это число объясняет отказ ДО того, как человек
     * его получит.
     *
     * Вычисляемое, а не поле класса: право правки приходит миксином из хранилища,
     * а поля класса считаются до того, как миксины появятся.
     */
    get options() {
        return {
            editable: this.$_canManageInventory,
            deletable: false,
            columns: [
                {
                    size: 35,
                    label: 'ФИО',
                    display: (x) => x.full_name,
                },
                {
                    size: 13,
                    label: 'Вид',
                    display: (x) => InventoryHolderTypeTitle[x.type] ?? '-',
                },
                {
                    size: 25,
                    label: 'Комментарий',
                    display: (x) => x.comment ?? '-',
                },
                {
                    size: 15,
                    label: 'На руках',
                    display: (x) => (this.heldCount(x) ? `${this.heldCount(x)}` : '-'),
                },
                {
                    size: 12,
                    label: 'Активен',
                    display: (x) => (x.active ? 'Да' : 'Нет'),
                },
            ],
        };
    }

    /**
     * Сколько единиц техники числится за держателем.
     *
     * Приходит с бэкенда счётчиком (items_count). Отсутствие ключа считаем нулём:
     * на показ это влияет только прочерком в колонке, а запрет гашения держит сервер.
     *
     * @param {Object} holder
     * @returns {number}
     */
    heldCount(holder) {
        return Number(holder?.items_count ?? 0);
    }

    onSearch(searchString) {
        this.page = 1;
        this.searchString = searchString;
    }

    onPageChange(page) {
        this.page = page;
    }

    onActiveFilterSelected(value) {
        this.page = 1;
        this.activeFilter = value;
    }

    async onCreateButtonClick() {
        let holder;

        try {
            holder = await this.$showModal(ModalNames.INVENTORY_HOLDER_CARD, {
                insert: true,
                holder: {
                    full_name: '',
                    type: InventoryHolderType.EXTERNAL,
                    comment: '',
                    active: true,
                },
            });
        } catch (error) {
            // The modal was closed.
            return;
        }

        this.isLoading = true;

        try {
            await createInventoryHolder(holder);
            await this.fetchHolders();
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    async onHolderClick(row) {
        // Без права правки строка не открывается: здесь нет режима просмотра,
        // и форма всё равно упала бы 403 при сохранении. Для наблюдателя страница
        // остаётся списком.
        if (!this.$_canManageInventory) {
            return;
        }

        let holder;

        try {
            holder = await this.$showModal(ModalNames.INVENTORY_HOLDER_CARD, { holder: { ...row } });
        } catch (error) {
            // The modal was closed.
            return;
        }

        this.isLoading = true;

        try {
            await updateInventoryHolder(holder);
            await this.fetchHolders();
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    @Watch('page')
    @Watch('searchString')
    @Watch('activeFilter')
    async fetchHolders() {
        this.isLoading = true;

        try {
            const query = {};

            if (this.activeFilter !== null) {
                query.active = this.activeFilter;
            }

            const response = await paginateInventoryHolders(this.page, this.searchString, query);

            this.rows = response.data.data;
            this.total = response.data.total;
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    created() {
        this.fetchHolders();
    }
}

export default InventoryPageHolders;
</script>
<style lang="scss"></style>
